import type { CookieSetOptions } from 'universal-cookie';
import Cookies from 'universal-cookie';
import { CONFIG } from '@frameio/config';
import { isTest } from '../config/helpers';
import { decodeBase64, encodeBase64 } from '../encode';
import { auth } from './auth';
import {
  IDENTIFIED_USER_COOKIE,
  IDENTIFIED_USER_SESSION_ID_COOKIE,
  SERVER_SIDE_ACCESS_TOKEN_EXPIRATION_BUFFER_SECONDS,
  X_IDENTIFIED_USER_HEADER,
  X_IDENTIFIED_USER_SESSION_ID_HEADER,
  X_SHARE_AUTHORIZATION_HEADER,
} from './constants';
import { encodeHeaderStringBase64 } from './encodeHeaderStringBase64';

export {
  encodeHeaderStringBase64,
  IDENTIFIED_USER_COOKIE,
  IDENTIFIED_USER_SESSION_ID_COOKIE,
  X_IDENTIFIED_USER_HEADER,
  X_IDENTIFIED_USER_SESSION_ID_HEADER,
  X_SHARE_AUTHORIZATION_HEADER,
};

// This is a workaround to fix an odd edge case bug where a path scoped
// cookie does not get exposed to JS when the page was loaded via a
// redirect for unclear reasons (httponly = false, samesite = lax, path is same)
// Note: the cookie works fine on reload so this is only needed for current session
const LOCAL_PASSPHRASE_BACKUP: { [shareId: string]: string } = {};

const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Store the passphrase in a cookie so that if a user has already entered it,
 * they don't need to enter it again
 *
 * This will also be where the apollo link gets the passphrase from
 *
 * @param shareId current id of the share that is being viewed
 * @param passphrase passphrase entered by user
 */
export const setPassphraseById = (
  shareId: string,
  passphrase: string
): void => {
  const cookies = new Cookies(shareId);
  const cookieParams = getCookieParams(shareId);
  const encodedPassphrase = encodeBase64(passphrase);

  // set for /share/:shareId, etc.
  cookies.set(shareId, encodedPassphrase, cookieParams);
  LOCAL_PASSPHRASE_BACKUP[shareId] = encodedPassphrase;
};

/**
 * Get the passphrase from a share id
 *
 * @param id id of share currently being viewed
 * @returns passphrase stored (if at all)
 */
export const getPassphraseById = (id: string): string | undefined => {
  const cookies = new Cookies(id);
  const sharePassphrase = cookies.get(id) || LOCAL_PASSPHRASE_BACKUP[id];
  const decodedPassphrase = decodeBase64(sharePassphrase);

  return decodedPassphrase;
};

/**
 * Store the identified user id in a cookie, so that we can attach a header
 * to requests and allow the user without an account to make mutations.
 *
 * @param shareId current id of the share that is being viewed
 * @param userId identified userId received from identifyUserMutation
 */
export const setIdentifiedUserId = (shareId: string, userId: string): void => {
  const cookies = new Cookies(IDENTIFIED_USER_COOKIE);
  const cookieParams = getCookieParams(shareId);

  cookies.set(IDENTIFIED_USER_COOKIE, userId, cookieParams);
};

/**
 * Retrieve identified user cookie
 *
 * @returns identified userId
 */
export const getIdentifiedUserId = () => {
  const cookies = new Cookies();
  const identifiedUserIdCookie = cookies.get(IDENTIFIED_USER_COOKIE);

  return identifiedUserIdCookie;
};

/**
 * Remove the cookie associated with an identified user, if one exists.
 *
 * @param shareId current id of the share that is being viewed
 */
export const removeIdentifiedUserId = (shareId: string): void => {
  const cookies = new Cookies();
  const cookieParams = getCookieParams(shareId);

  cookies.remove(IDENTIFIED_USER_COOKIE, cookieParams);
};

/**
 * Retrieve the identified user session id cookie without generating a new one
 *
 * @returns the identified user session id cookie
 */
export const getIdentifiedUserSessionId = () => {
  const cookies = new Cookies();
  return cookies.get(IDENTIFIED_USER_SESSION_ID_COOKIE);
};

/**
 * Create and store the identified user session id cookie, if it doesn't already exist
 *
 * @returns the identified user session id (either existing or newly created)
 */
export const setIdentifiedUserSessionId = () => {
  // If the identified user session id already exists, don't create a new one
  if (getIdentifiedUserSessionId()) return;

  const cookies = new Cookies();
  const identifiedUserSessionId = crypto.randomUUID();

  const cookieParams: CookieSetOptions = {
    domain: CONFIG.cookieDomain,
    expires: new Date(Date.now() + ONE_WEEK_IN_MS),
    path: '/', // this allows the cookie to be used across all paths
    sameSite: 'lax',
    secure: true, // this only allows the cookie to be set over HTTPS
  };

  cookies.set(
    IDENTIFIED_USER_SESSION_ID_COOKIE,
    identifiedUserSessionId,
    cookieParams
  );
};

/**
 * Get the cookie settings that should be used when setting share auth cookies
 *
 * @environments client
 */
const getCookieParams = (shareId: string): CookieSetOptions => {
  // Don't scope cookies in tests, because then they can't be read.
  if (isTest()) return {};

  return {
    domain: CONFIG.cookieDomain,
    expires: undefined, // this sets the cookie to expire at end of the Session
    path: `/share/${shareId}`, // this only loads the cookie for a matching path (this will match for /share/:share-id/:folder-id/etc)
    sameSite: 'lax',
    secure: true, // this only allows the cookie to be set over HTTPS
  };
};

/**
 * Get the header for a share query with the appropriate authorization headers
 *
 * @param shareId the current share ID
 * @param currentServersideAccessToken the existing access token
 * @returns
 */
export const getAuthorizedShareHeaders = async (
  shareId: string,
  serverSideAccessTokenProp: string | undefined | null = null
): Promise<Record<string, string>> => {
  const passphrase = getPassphraseById(shareId);
  let serverSideAccessToken: string | undefined | null =
    serverSideAccessTokenProp;
  if (!serverSideAccessToken) {
    serverSideAccessToken = await auth.getAccessTokenWithRefreshIfExpired(
      SERVER_SIDE_ACCESS_TOKEN_EXPIRATION_BUFFER_SECONDS
    );
  }
  const headers: Record<string, string> = {
    ...(serverSideAccessToken && {
      authorization: `Bearer ${serverSideAccessToken}`,
    }),
  };

  const knownShareToken = auth.getShareToken();
  if (knownShareToken) headers[X_SHARE_AUTHORIZATION_HEADER] = knownShareToken;

  if (shareId) {
    headers[X_SHARE_AUTHORIZATION_HEADER] = encodeHeaderStringBase64(
      shareId,
      passphrase
    );
  }
  return headers;
};

export const getShareAuthHeaders = (
  shareId?: string,
  {
    encodedPassphrase,
    identifiedUserId,
    identifiedUserSessionId,
  }: {
    encodedPassphrase?: string;
    identifiedUserId?: string;
    identifiedUserSessionId?: string;
  } = {}
) => {
  if (!shareId) return {};
  return {
    [X_SHARE_AUTHORIZATION_HEADER]: encodeHeaderStringBase64(
      shareId,
      encodedPassphrase ? decodeBase64(encodedPassphrase) : undefined
    ),
    ...(identifiedUserId && { [X_IDENTIFIED_USER_HEADER]: identifiedUserId }),
    ...(identifiedUserSessionId && {
      [X_IDENTIFIED_USER_SESSION_ID_HEADER]: identifiedUserSessionId,
    }),
  };
};
